import React from 'react';
import { Control, Controller, FieldErrors, UseFormWatch, UseFormRegister } from 'react-hook-form';
import { Form, Input, Button, Select } from '@crisellevaldez/speculo-ui';
import { Settings } from 'lucide-react';
import { BrandingFormData } from '../../../../../schemas/branding.schema';
import { IconPill } from '../../../../common/IconPill';

interface LogoSizes {
  small: { width: number; height: number };
  medium: { width: number; height: number };
  large: { width: number; height: number };
}

interface GeneralSettingsSectionProps {
  control: Control<BrandingFormData>;
  register: UseFormRegister<BrandingFormData>;
  errors: FieldErrors<BrandingFormData>;
  watch: UseFormWatch<BrandingFormData>;
  logoUrl: string;
  faviconUrl: string;
  selectedFile: File | null;
  selectedFavicon: File | null;
  logoSize: 'small' | 'medium' | 'large';
  logoPlacement: 'header' | 'both';
  logoSizes: LogoSizes;
  onLogoSizeChange: (size: 'small' | 'medium' | 'large') => void;
  onLogoPlacementChange: (placement: 'header' | 'both') => void;
  onFileSelect: (file: File | null) => void;
  onFaviconSelect: (file: File | null) => void;
}

export const GeneralSettingsSection: React.FC<GeneralSettingsSectionProps> = ({
  control,
  register,
  errors,
  watch,
  logoUrl,
  faviconUrl,
  selectedFile,
  selectedFavicon,
  logoSize,
  logoPlacement,
  logoSizes,
  onLogoSizeChange,
  onLogoPlacementChange,
  onFileSelect,
  onFaviconSelect,
}) => {
  return (
    <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
      <div className="flex items-center gap-3 mb-3">
        <IconPill icon={Settings} />
        <h4 className="text-sm font-bold text-foreground">General Settings</h4>
      </div>

      <hr className="dark:border-white/10" />

      <div className="space-y-6 mt-5">
        {/* Website/Team Name with Checkbox */}
        <Form.Control required>
          <Form.Label>Website/Team Name</Form.Label>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                {...register('name')}
                error={errors.name?.message}
                placeholder="Enter website or team name"
              />
            </div>
            <div className="flex items-center gap-2">
              <Controller
                name="teamName"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="showOnHomePage"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked ? watch('name') : '')}
                    className="h-4 w-4 rounded border-gray-300 dark:border-white/15 text-primary focus:ring-primary"
                  />
                )}
              />
              <label htmlFor="showOnHomePage" className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Show on home page
              </label>
            </div>
          </div>
          <Form.HelperText>Added to the footer of the IDX site by default</Form.HelperText>
        </Form.Control>

        {/* Logo and Favicon Upload */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <Form.Control required>
            <Form.Label>Logo</Form.Label>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                >
                  Browse
                </Button>
                <Select
                  value={logoSize}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      onLogoSizeChange(value as 'small' | 'medium' | 'large');
                    }
                  }}
                  options={[
                    { value: 'small', label: 'Small' },
                    { value: 'medium', label: 'Medium ' },
                    { value: 'large', label: 'Large' },
                  ]}
                />
                <Select
                  value={logoPlacement}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      onLogoPlacementChange(value as 'header' | 'both');
                    }
                  }}
                  options={[
                    { value: 'header', label: 'Header Only' },
                    { value: 'both', label: 'Header & Footer' },
                  ]}
                />
              </div>
              {(selectedFile || logoUrl) && (
                <div className="flex justify-start">
                  <div
                    className="rounded-md overflow-hidden bg-gray-50 dark:bg-white/5"
                    style={{
                      width: logoSizes[logoSize].width,
                      height: logoSizes[logoSize].height,
                    }}
                  >
                    <img
                      src={selectedFile ? URL.createObjectURL(selectedFile) : logoUrl}
                      alt="Logo preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
            <input
              id="logo-upload"
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
            />
          </Form.Control>

          <Form.Control>
            <Form.Label>Favicon</Form.Label>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => document.getElementById('favicon-upload')?.click()}
              >
                Browse
              </Button>
              {(selectedFavicon || faviconUrl) && (
                <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-50 dark:bg-white/5">
                  <img
                    src={selectedFavicon ? URL.createObjectURL(selectedFavicon) : faviconUrl}
                    alt="Favicon preview"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>
            <input
              id="favicon-upload"
              type="file"
              className="hidden"
              accept="image/x-icon,image/png"
              onChange={(e) => onFaviconSelect(e.target.files?.[0] || null)}
            />
          </Form.Control>
        </div>

        {/* Branding Colors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            name="colors.primary"
            control={control}
            render={({ field }) => (
              <Form.Control required>
                <Form.Label>Primary Color</Form.Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="h-8 w-8 rounded-md cursor-pointer border border-gray-200 dark:border-white/10 flex-shrink-0"
                  />
                  <Input
                    {...field}
                    className="uppercase"
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    error={errors.colors?.primary?.message}
                  />
                </div>
              </Form.Control>
            )}
          />

          <Controller
            name="colors.secondary"
            control={control}
            render={({ field }) => (
              <Form.Control required>
                <Form.Label>Secondary Color</Form.Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="h-8 w-8 rounded-md cursor-pointer border border-gray-200 dark:border-white/10 flex-shrink-0"
                  />
                  <Input
                    {...field}
                    className="uppercase"
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    error={errors.colors?.secondary?.message}
                  />
                </div>
              </Form.Control>
            )}
          />
        </div>
      </div>
    </div>
  );
};
